// ============================================================================
// `@torre/costos` — Presupuesto, Costos y Márgenes (issues #28, #29, #30).
// ============================================================================
// Paquete puro, agnóstico al framework y al tenant. Es el **motor de cálculo**
// del módulo: el front (`apps/v5`) trae los datos crudos del backend y toda la
// aritmética de negocio ocurre aquí, en funciones puras y testeadas.
//
//   • CONSTANTES — nombres de colección Firestore y vocabularios (enums).
//   • TIPOS      — transacción de costo, línea de presupuesto, ingreso, unidad.
//   • CÁLCULO    — ejecución presupuestaria (#28), prorrateo/distribución por
//                  dimensión con drill-down (#29), margen bruto y de
//                  contribución real vs presupuesto y por hectárea (#30).
//   • EXPORT     — serialización CSV de cualquier reporte (#29).
//   • CONTRATO   — rutas del servicio HTTP e interfaz `CostosService`.
//
// Diseño (playbook de extracción @torre/*): sin `process.env`/`import.meta.env`
// en import, sin `axios`, sin `firebase`, sin triggers. `db`/config se inyectan
// desde el consumidor. Solo funciones puras y datos.
//
// Las **dimensiones** (área operativa, centro de costo, tipo de gasto) NO se
// definen aquí: son el maestro de `@torre/configuracion` (issue #3, PR #88) y
// este paquete solo las referencia por id/nombre. El cuartel es el maestro de
// Cuarteles (#5). Este módulo no duplica ningún catálogo.
// ============================================================================

export const TORRE_COSTOS_VERSION = "0.1.0";

// ---------------------------------------------------------------------------
// CONSTANTES — colecciones Firestore (fuente única de verdad, espejada por el
// controlador del backend).
// ---------------------------------------------------------------------------

/** Transacciones de costo real, ya imputadas a sus dimensiones (#29). */
export const COSTOS_TRANSACCIONES_COLLECTION = "costos_transacciones";
/** Líneas del presupuesto anual, por periodo y centro de costo (#28). */
export const PRESUPUESTO_LINEAS_COLLECTION = "presupuesto_lineas";
/** Ingresos (venta/producción valorizada) por cultivo/unidad (#30). */
export const COSTOS_INGRESOS_COLLECTION = "costos_ingresos";
/** Unidades productivas con su superficie, para los resultados por ha (#30). */
export const COSTOS_UNIDADES_COLLECTION = "costos_unidades";

/** Las cuatro colecciones que componen el módulo de costos. */
export const COSTOS_COLLECTIONS = [
  COSTOS_TRANSACCIONES_COLLECTION,
  PRESUPUESTO_LINEAS_COLLECTION,
  COSTOS_INGRESOS_COLLECTION,
  COSTOS_UNIDADES_COLLECTION,
] as const;

// ---------------------------------------------------------------------------
// VOCABULARIOS (enums) — compartidos por selects, badges y validación.
// ---------------------------------------------------------------------------

/** De dónde nace el costo real. Alimenta el drill-down a la transacción (#29). */
export const ORIGENES_COSTO = [
  "labor",
  "insumo",
  "maquinaria",
  "compra",
  "servicio",
  "otro",
] as const;
export type OrigenCosto = (typeof ORIGENES_COSTO)[number];

/**
 * Dimensiones por las que se puede distribuir el gasto (#29). El maestro de
 * `area`, `centroCosto` y `tipoGasto` vive en `@torre/configuracion` (#3); el
 * de `cuartel` en Cuarteles (#5); `cultivo` es el atributo de la campaña.
 */
export const DIMENSIONES_COSTO = [
  "area",
  "centroCosto",
  "cuartel",
  "tipoGasto",
  "cultivo",
] as const;
export type DimensionCosto = (typeof DIMENSIONES_COSTO)[number];

/** Id sintético del grupo que agrupa lo no imputado a una dimensión. */
export const SIN_ASIGNAR_ID = "sin-asignar";

// ---------------------------------------------------------------------------
// TIPOS — el dato crudo que entra al motor.
// ---------------------------------------------------------------------------

/** Puntero a la transacción de origen, para el drill-down del reporte (#29). */
export interface ReferenciaOrigen {
  /** Módulo/colección de origen ("compras", "faenas", "movimientos_bodega"…). */
  tipo: string;
  /** Id del documento de origen. */
  id: string;
  /** Texto corto para mostrar en el drill-down (folio, faena, producto…). */
  detalle?: string | null;
}

/**
 * Una transacción de costo real ya imputada a sus dimensiones. Es la unidad
 * atómica de todos los reportes: presupuesto (#28), distribución (#29) y
 * márgenes (#30) se calculan sumando estas filas.
 */
export interface CostoTransaccion {
  id: string;
  company_id?: string | null;
  /** Periodo contable: "2026" (anual) o "2026-07" (mensual). */
  periodo: string;
  /** Fecha ISO del gasto (opcional; el periodo es lo que agrupa). */
  fecha?: string | null;

  // --- Dimensiones (referencias al maestro; nunca se duplica el catálogo) ---
  areaId?: string | null;
  areaNombre?: string | null;
  centroCostoId?: string | null;
  centroCostoNombre?: string | null;
  cuartelId?: string | null;
  cuartelNombre?: string | null;
  tipoGastoId?: string | null;
  tipoGastoNombre?: string | null;
  cultivoId?: string | null;
  cultivoNombre?: string | null;

  /** Qué generó el costo (labor, insumo, maquinaria…). */
  origen: OrigenCosto;
  /**
   * Costo directo (imputable a un cultivo/unidad: labores e insumos) vs.
   * indirecto (estructura). Define el margen bruto (#30).
   */
  directo: boolean;
  /**
   * Costo variable (varía con el nivel de actividad). Define el margen de
   * contribución (#30). Si no se informa, se asume igual a `directo`.
   */
  variable?: boolean | null;

  descripcion?: string | null;
  /** Monto del gasto en la moneda base. Debe ser finito y >= 0. */
  monto: number;
  /** Transacción de origen, para profundizar desde el reporte (#29). */
  referencia?: ReferenciaOrigen | null;
}

/**
 * Una línea del presupuesto anual: cuánto se planificó gastar en un periodo,
 * para un centro de costo (y, opcionalmente, un cultivo o tipo de gasto).
 */
export interface PresupuestoLinea {
  id: string;
  company_id?: string | null;
  /** Periodo presupuestado: "2026" o "2026-07". */
  periodo: string;
  centroCostoId?: string | null;
  centroCostoNombre?: string | null;
  areaId?: string | null;
  cultivoId?: string | null;
  cultivoNombre?: string | null;
  tipoGastoId?: string | null;
  /** Clasificación contable directo/indirecto del monto presupuestado. */
  directo?: boolean | null;
  /** Monto planificado en la moneda base. Debe ser finito y >= 0. */
  montoPresupuestado: number;
  notas?: string | null;
}

/** Ingreso (venta o producción valorizada) imputado a un cultivo/unidad (#30). */
export interface IngresoRegistro {
  id: string;
  company_id?: string | null;
  periodo: string;
  cultivoId?: string | null;
  cultivoNombre?: string | null;
  cuartelId?: string | null;
  descripcion?: string | null;
  /** Ingreso real percibido/valorizado. */
  monto: number;
  /** Ingreso planificado para el mismo cultivo/periodo (comparativo). */
  montoPresupuestado?: number | null;
}

/** Unidad productiva con superficie, para expresar resultados por hectárea. */
export interface UnidadProductiva {
  id: string;
  nombre: string;
  cultivoId?: string | null;
  cultivoNombre?: string | null;
  /** Superficie en hectáreas. <= 0 ⇒ los "por ha" quedan en null. */
  hectareas: number;
}

// ---------------------------------------------------------------------------
// HELPERS numéricos — el redondeo es explícito y compartido para que el front,
// el CSV y los tests no diverjan por decimales.
// ---------------------------------------------------------------------------

/** Redondea a `decimales` (por defecto 2) de forma estable. */
export function redondear(n: number, decimales = 2): number {
  if (!Number.isFinite(n)) return 0;
  const f = 10 ** decimales;
  // El +Number.EPSILON evita el clásico 1.005 → 1.00 del binario flotante.
  return Math.round((n + Number.EPSILON) * f) / f;
}

/** Suma segura: ignora montos no finitos en vez de propagar NaN. */
export function sumarMontos<T>(rows: readonly T[], pick: (r: T) => number): number {
  return redondear(
    rows.reduce((acc, r) => {
      const v = pick(r);
      return Number.isFinite(v) ? acc + v : acc;
    }, 0)
  );
}

/**
 * Porcentaje `parte / total * 100`. Devuelve `null` cuando el total es 0 o no
 * es válido: un porcentaje sobre una base cero no existe y no debe inventarse.
 */
export function porcentaje(parte: number, total: number): number | null {
  if (!Number.isFinite(parte) || !Number.isFinite(total) || total === 0) return null;
  return redondear((parte / total) * 100);
}

/** División por hectáreas: `null` si la superficie no es utilizable. */
export function porHectarea(monto: number, hectareas: number): number | null {
  if (!Number.isFinite(monto) || !Number.isFinite(hectareas) || hectareas <= 0) {
    return null;
  }
  return redondear(monto / hectareas);
}

/** Un costo es variable si se declara; si no, se asume igual a su clasificación directa. */
export function esVariable(t: CostoTransaccion): boolean {
  return t.variable === null || t.variable === undefined ? t.directo : !!t.variable;
}

// ---------------------------------------------------------------------------
// FILTRO — la base del drill-down (#29) y del recorte por periodo (#28/#30).
// ---------------------------------------------------------------------------

export interface FiltroCostos {
  /** Periodo exacto ("2026-07") o prefijo de año ("2026" matchea "2026-07"). */
  periodo?: string | null;
  areaId?: string | null;
  centroCostoId?: string | null;
  cuartelId?: string | null;
  tipoGastoId?: string | null;
  cultivoId?: string | null;
  origen?: OrigenCosto | null;
}

/**
 * `"2026"` matchea `"2026"` y `"2026-07"` (un presupuesto anual contra gastos
 * mensuales); `"2026-07"` matchea solo ese mes.
 */
export function periodoIncluye(filtro: string, periodo: string): boolean {
  if (!filtro) return true;
  if (filtro === periodo) return true;
  return periodo.startsWith(`${filtro}-`);
}

/** Aplica el filtro a las transacciones. Un campo ausente/nulo no filtra. */
export function filtrarTransacciones(
  transacciones: readonly CostoTransaccion[],
  filtro: FiltroCostos = {}
): CostoTransaccion[] {
  return transacciones.filter((t) => {
    if (filtro.periodo && !periodoIncluye(filtro.periodo, t.periodo)) return false;
    if (filtro.areaId && t.areaId !== filtro.areaId) return false;
    if (filtro.centroCostoId && t.centroCostoId !== filtro.centroCostoId) return false;
    if (filtro.cuartelId && t.cuartelId !== filtro.cuartelId) return false;
    if (filtro.tipoGastoId && t.tipoGastoId !== filtro.tipoGastoId) return false;
    if (filtro.cultivoId && t.cultivoId !== filtro.cultivoId) return false;
    if (filtro.origen && t.origen !== filtro.origen) return false;
    return true;
  });
}

// ---------------------------------------------------------------------------
// #29 — DISTRIBUCIÓN DE COSTOS POR DIMENSIÓN (área / centro de costo / cuartel)
// ---------------------------------------------------------------------------

/** Lee el par (id, nombre) de una transacción para una dimensión dada. */
export function clavesDimension(
  t: CostoTransaccion,
  dimension: DimensionCosto
): { id: string | null; nombre: string | null } {
  switch (dimension) {
    case "area":
      return { id: t.areaId ?? null, nombre: t.areaNombre ?? null };
    case "centroCosto":
      return { id: t.centroCostoId ?? null, nombre: t.centroCostoNombre ?? null };
    case "cuartel":
      return { id: t.cuartelId ?? null, nombre: t.cuartelNombre ?? null };
    case "tipoGasto":
      return { id: t.tipoGastoId ?? null, nombre: t.tipoGastoNombre ?? null };
    case "cultivo":
      return { id: t.cultivoId ?? null, nombre: t.cultivoNombre ?? null };
  }
}

/** Un grupo del reporte de distribución. */
export interface GrupoCosto {
  /** Id de la dimensión, o `SIN_ASIGNAR_ID` si la transacción no la informa. */
  id: string;
  nombre: string;
  monto: number;
  /** Participación sobre el total del reporte. `null` si el total es 0. */
  porcentaje: number | null;
  /** Cuántas transacciones componen el grupo (tamaño del drill-down). */
  transacciones: number;
}

export interface DistribucionCostos {
  dimension: DimensionCosto;
  total: number;
  grupos: GrupoCosto[];
}

/**
 * #29 · Distribuye el gasto por una dimensión, con su % sobre el total.
 *
 * 1) Recolecta las transacciones del periodo (vía `filtro`).
 * 2) Las clasifica por la dimensión pedida (lo no imputado cae en "sin asignar",
 *    visible a propósito: un gasto sin dimensión es un dato a corregir).
 * 3) Suma y calcula el porcentaje sobre el total.
 * Los grupos vienen ordenados de mayor a menor gasto (lo que duele primero).
 */
export function distribuirCostos(
  transacciones: readonly CostoTransaccion[],
  dimension: DimensionCosto,
  filtro: FiltroCostos = {}
): DistribucionCostos {
  const rows = filtrarTransacciones(transacciones, filtro);
  const total = sumarMontos(rows, (r) => r.monto);

  const acc = new Map<string, { nombre: string; monto: number; n: number }>();
  for (const t of rows) {
    const { id, nombre } = clavesDimension(t, dimension);
    const key = id || SIN_ASIGNAR_ID;
    const prev = acc.get(key);
    const label = nombre || (id ? id : SIN_ASIGNAR_ID);
    if (prev) {
      prev.monto += Number.isFinite(t.monto) ? t.monto : 0;
      prev.n += 1;
    } else {
      acc.set(key, {
        nombre: label,
        monto: Number.isFinite(t.monto) ? t.monto : 0,
        n: 1,
      });
    }
  }

  const grupos: GrupoCosto[] = [...acc.entries()]
    .map(([id, g]) => ({
      id,
      nombre: g.nombre,
      monto: redondear(g.monto),
      porcentaje: porcentaje(g.monto, total),
      transacciones: g.n,
    }))
    .sort((a, b) => b.monto - a.monto || a.nombre.localeCompare(b.nombre));

  return { dimension, total, grupos };
}

/** Un grupo de primer nivel con su desglose de segundo nivel. */
export interface GrupoCostoAnidado extends GrupoCosto {
  /** Desglose interno (p. ej. el cuartel abierto por centro de costo). */
  desglose: GrupoCosto[];
}

export interface DistribucionAnidada {
  dimension: DimensionCosto;
  subDimension: DimensionCosto;
  total: number;
  grupos: GrupoCostoAnidado[];
}

/**
 * #29 · "Gasto por cuartel desglosado por centro de costo": distribuye por una
 * dimensión y abre cada grupo por una segunda. El `porcentaje` del desglose es
 * relativo a **su grupo padre** (cuánto del cuartel se fue en ese centro), no
 * al total del predio — que es lo que el usuario está leyendo en esa fila.
 */
export function distribuirCostosAnidado(
  transacciones: readonly CostoTransaccion[],
  dimension: DimensionCosto,
  subDimension: DimensionCosto,
  filtro: FiltroCostos = {}
): DistribucionAnidada {
  const rows = filtrarTransacciones(transacciones, filtro);
  const nivel1 = distribuirCostos(rows, dimension);

  const grupos: GrupoCostoAnidado[] = nivel1.grupos.map((g) => {
    const hijos = rows.filter(
      (t) => (clavesDimension(t, dimension).id || SIN_ASIGNAR_ID) === g.id
    );
    return { ...g, desglose: distribuirCostos(hijos, subDimension).grupos };
  });

  return { dimension, subDimension, total: nivel1.total, grupos };
}

/**
 * #29 · Drill-down: las transacciones de origen de un grupo del reporte, para
 * abrir una fila hasta el documento que la generó.
 */
export function transaccionesDeGrupo(
  transacciones: readonly CostoTransaccion[],
  dimension: DimensionCosto,
  grupoId: string,
  filtro: FiltroCostos = {}
): CostoTransaccion[] {
  return filtrarTransacciones(transacciones, filtro).filter(
    (t) => (clavesDimension(t, dimension).id || SIN_ASIGNAR_ID) === grupoId
  );
}

// ---------------------------------------------------------------------------
// #28 — EJECUCIÓN DEL PRESUPUESTO ANUAL (presupuestado vs. gastado real)
// ---------------------------------------------------------------------------

/** Ejecución de un centro de costo dentro del presupuesto. */
export interface EjecucionCentro {
  centroCostoId: string;
  centroCostoNombre: string;
  presupuestado: number;
  gastado: number;
  /** `presupuestado - gastado`. Negativo ⇒ sobregiro. */
  diferencia: number;
  /** % de ejecución. `null` cuando no hay presupuesto contra el cual medir. */
  porcentajeEjecucion: number | null;
  /** El gasto superó lo presupuestado (incluye gasto sin presupuesto). */
  sobregiro: boolean;
}

export interface EjecucionPresupuesto {
  periodo: string | null;
  totalPresupuestado: number;
  totalGastado: number;
  /** `totalPresupuestado - totalGastado`. Negativo ⇒ sobregiro global. */
  diferencia: number;
  /** % de gasto sobre el presupuesto. `null` si no hay presupuesto cargado. */
  porcentajeEjecucion: number | null;
  sobregiro: boolean;
  porCentroCosto: EjecucionCentro[];
}

/**
 * #28 · Lógica de decisión del plan:
 *   1. Cargar el presupuesto anual por periodo y centro de costo.
 *   2. Acumular el gasto real (compras, movimientos y faenas ya imputados).
 *   3. Calcular diferencia y porcentaje de ejecución.
 *   4. Si el gasto supera el presupuesto, destacar el sobregiro.
 *
 * Los centros con gasto pero **sin presupuesto** aparecen igual (presupuestado
 * 0, `sobregiro: true`, `porcentajeEjecucion: null`): esconderlos daría un
 * total que no cuadra con la contabilidad.
 */
export function calcularEjecucionPresupuesto(input: {
  lineas: readonly PresupuestoLinea[];
  transacciones: readonly CostoTransaccion[];
  /** Periodo a evaluar. "2026" incluye los gastos "2026-MM". */
  periodo?: string | null;
}): EjecucionPresupuesto {
  const periodo = input.periodo ?? null;

  const lineas = periodo
    ? input.lineas.filter((l) => periodoIncluye(periodo, l.periodo))
    : [...input.lineas];
  const gastos = filtrarTransacciones(input.transacciones, { periodo });

  const totalPresupuestado = sumarMontos(lineas, (l) => l.montoPresupuestado);
  const totalGastado = sumarMontos(gastos, (t) => t.monto);

  // Un mapa por centro de costo alimentado desde ambos lados (presupuesto y
  // gasto), de modo que ningún centro se pierda por estar en uno solo.
  const acc = new Map<string, { nombre: string; presupuestado: number; gastado: number }>();
  const ensure = (id: string | null | undefined, nombre?: string | null) => {
    const key = id || SIN_ASIGNAR_ID;
    let row = acc.get(key);
    if (!row) {
      row = { nombre: nombre || key, presupuestado: 0, gastado: 0 };
      acc.set(key, row);
    } else if (nombre && row.nombre === key) {
      row.nombre = nombre;
    }
    return row;
  };

  for (const l of lineas) {
    const row = ensure(l.centroCostoId, l.centroCostoNombre);
    if (Number.isFinite(l.montoPresupuestado)) row.presupuestado += l.montoPresupuestado;
  }
  for (const t of gastos) {
    const row = ensure(t.centroCostoId, t.centroCostoNombre);
    if (Number.isFinite(t.monto)) row.gastado += t.monto;
  }

  const porCentroCosto: EjecucionCentro[] = [...acc.entries()]
    .map(([id, r]) => {
      const presupuestado = redondear(r.presupuestado);
      const gastado = redondear(r.gastado);
      return {
        centroCostoId: id,
        centroCostoNombre: r.nombre,
        presupuestado,
        gastado,
        diferencia: redondear(presupuestado - gastado),
        porcentajeEjecucion: porcentaje(gastado, presupuestado),
        sobregiro: gastado > presupuestado,
      };
    })
    .sort((a, b) => b.gastado - a.gastado || a.centroCostoNombre.localeCompare(b.centroCostoNombre));

  return {
    periodo,
    totalPresupuestado,
    totalGastado,
    diferencia: redondear(totalPresupuestado - totalGastado),
    porcentajeEjecucion: porcentaje(totalGastado, totalPresupuestado),
    sobregiro: totalGastado > totalPresupuestado,
    porCentroCosto,
  };
}

// ---------------------------------------------------------------------------
// #30 — MARGEN BRUTO Y DE CONTRIBUCIÓN POR CULTIVO/UNIDAD
// ---------------------------------------------------------------------------

/** El resultado económico de un cultivo/unidad en un periodo. */
export interface MargenResultado {
  /** Id del cultivo (o de la unidad productiva, según cómo se agrupe). */
  id: string;
  nombre: string;
  /** Superficie sumada de las unidades del grupo. */
  hectareas: number;

  ingresos: number;
  costosDirectos: number;
  costosIndirectos: number;
  costosVariables: number;
  costosTotales: number;

  /** `ingresos - costosDirectos`. */
  margenBruto: number;
  /** `ingresos - costosVariables`. */
  margenContribucion: number;
  /** `ingresos - costosTotales` (resultado operativo del cultivo). */
  utilidad: number;

  // --- Comparativo real vs presupuesto (#30) ---
  ingresosPresupuestados: number;
  costosDirectosPresupuestados: number;
  /** Margen bruto planificado: `ingresosPresup - costosDirectosPresup`. */
  margenBrutoPresupuestado: number;
  /** `margenBruto - margenBrutoPresupuestado`. Negativo ⇒ peor que el plan. */
  desvio: number;
  /** Desvío como % del margen presupuestado. `null` si el plan es 0. */
  desvioPorcentaje: number | null;

  // --- Resultados por hectárea (`null` si no hay superficie cargada) ---
  ingresosPorHa: number | null;
  costosDirectosPorHa: number | null;
  margenBrutoPorHa: number | null;
  margenContribucionPorHa: number | null;
  /** Desvío por ha contra el presupuesto — el KPI comparativo de CERES. */
  desvioPorHa: number | null;
}

export interface MargenesReporte {
  periodo: string | null;
  resultados: MargenResultado[];
  /** Fila de totales del predio (mismos campos, agregados). */
  totales: Omit<MargenResultado, "id" | "nombre">;
}

/**
 * #30 · Lógica de decisión del plan:
 *   1) Sumar ingresos por cultivo/unidad.
 *   2) Sumar costos directos de labores e insumos.
 *   3) Margen = ingresos - costos directos.
 *   4) Calcular desvío contra presupuesto.
 *   5) Dividir entre hectáreas para el valor por ha.
 *
 * Se agrupa por `cultivoId` (lo que el gerente compara). Las unidades aportan
 * la superficie del cultivo. Lo no imputado a un cultivo cae en "sin asignar":
 * sus costos indirectos son reales y deben verse, aunque no tengan margen.
 *
 * Un cultivo entra al reporte solo si tuvo **actividad** en el periodo (ingreso,
 * costo o presupuesto). Tener superficie cargada no basta: las unidades no
 * tienen periodo, y darlas de alta por sí solas llenaría de filas en cero
 * cualquier periodo sin movimiento. En consecuencia, `totales.hectareas` es la
 * superficie de los cultivos reportados — el "por ha" del predio se mide sobre
 * la tierra que produjo el resultado, no sobre la que no tuvo actividad.
 */
export function calcularMargenes(input: {
  transacciones: readonly CostoTransaccion[];
  ingresos: readonly IngresoRegistro[];
  unidades?: readonly UnidadProductiva[];
  /** Presupuesto de costos, para el comparativo real vs. plan. */
  presupuesto?: readonly PresupuestoLinea[];
  periodo?: string | null;
}): MargenesReporte {
  const periodo = input.periodo ?? null;
  const inPeriodo = (p: string) => (periodo ? periodoIncluye(periodo, p) : true);

  const costos = filtrarTransacciones(input.transacciones, { periodo });
  const ingresos = input.ingresos.filter((i) => inPeriodo(i.periodo));
  const presupuesto = (input.presupuesto ?? []).filter((l) => inPeriodo(l.periodo));
  const unidades = input.unidades ?? [];

  interface Acc {
    nombre: string;
    hectareas: number;
    ingresos: number;
    ingresosPresupuestados: number;
    costosDirectos: number;
    costosIndirectos: number;
    costosVariables: number;
    costosDirectosPresupuestados: number;
  }

  const acc = new Map<string, Acc>();
  const ensure = (id: string | null | undefined, nombre?: string | null): Acc => {
    const key = id || SIN_ASIGNAR_ID;
    let row = acc.get(key);
    if (!row) {
      row = {
        nombre: nombre || key,
        hectareas: 0,
        ingresos: 0,
        ingresosPresupuestados: 0,
        costosDirectos: 0,
        costosIndirectos: 0,
        costosVariables: 0,
        costosDirectosPresupuestados: 0,
      };
      acc.set(key, row);
    } else if (nombre && row.nombre === key) {
      row.nombre = nombre;
    }
    return row;
  };

  // 1) Superficie por cultivo (la base de todo "por ha"). Se indexa aparte y
  //    NO crea grupos: las unidades no tienen periodo, así que dar de alta un
  //    cultivo por su superficie llenaría de filas en cero cualquier periodo
  //    sin actividad. Solo un ingreso, un costo o un presupuesto abre grupo;
  //    la superficie se adjunta después a los que sí lo tienen.
  const superficie = new Map<string, number>();
  for (const u of unidades) {
    if (!Number.isFinite(u.hectareas) || u.hectareas <= 0) continue;
    const key = u.cultivoId || SIN_ASIGNAR_ID;
    superficie.set(key, (superficie.get(key) ?? 0) + u.hectareas);
  }

  // 2) Ingresos reales y presupuestados.
  for (const i of ingresos) {
    const row = ensure(i.cultivoId, i.cultivoNombre);
    if (Number.isFinite(i.monto)) row.ingresos += i.monto;
    const plan = i.montoPresupuestado;
    if (typeof plan === "number" && Number.isFinite(plan)) {
      row.ingresosPresupuestados += plan;
    }
  }

  // 3) Costos reales, separando directo/indirecto y variable.
  for (const t of costos) {
    const row = ensure(t.cultivoId, t.cultivoNombre);
    const monto = Number.isFinite(t.monto) ? t.monto : 0;
    if (t.directo) row.costosDirectos += monto;
    else row.costosIndirectos += monto;
    if (esVariable(t)) row.costosVariables += monto;
  }

  // 4) Costos directos presupuestados (solo las líneas marcadas como directas;
  //    el margen bruto planificado se mide contra costos directos).
  for (const l of presupuesto) {
    if (!l.directo) continue;
    const row = ensure(l.cultivoId, l.cultivoNombre);
    if (Number.isFinite(l.montoPresupuestado)) {
      row.costosDirectosPresupuestados += l.montoPresupuestado;
    }
  }

  // 5) La superficie se adjunta solo a los cultivos que sí tienen actividad.
  for (const [id, r] of acc) {
    r.hectareas = superficie.get(id) ?? 0;
  }

  const build = (id: string, r: Acc): MargenResultado => {
    const ing = redondear(r.ingresos);
    const dir = redondear(r.costosDirectos);
    const ind = redondear(r.costosIndirectos);
    const varb = redondear(r.costosVariables);
    const ha = redondear(r.hectareas, 4);

    const margenBruto = redondear(ing - dir);
    const margenContribucion = redondear(ing - varb);
    const costosTotales = redondear(dir + ind);

    const ingPlan = redondear(r.ingresosPresupuestados);
    const dirPlan = redondear(r.costosDirectosPresupuestados);
    const margenPlan = redondear(ingPlan - dirPlan);
    const desvio = redondear(margenBruto - margenPlan);

    return {
      id,
      nombre: r.nombre,
      hectareas: ha,
      ingresos: ing,
      costosDirectos: dir,
      costosIndirectos: ind,
      costosVariables: varb,
      costosTotales,
      margenBruto,
      margenContribucion,
      utilidad: redondear(ing - costosTotales),
      ingresosPresupuestados: ingPlan,
      costosDirectosPresupuestados: dirPlan,
      margenBrutoPresupuestado: margenPlan,
      desvio,
      desvioPorcentaje: porcentaje(desvio, Math.abs(margenPlan)),
      ingresosPorHa: porHectarea(ing, ha),
      costosDirectosPorHa: porHectarea(dir, ha),
      margenBrutoPorHa: porHectarea(margenBruto, ha),
      margenContribucionPorHa: porHectarea(margenContribucion, ha),
      desvioPorHa: porHectarea(desvio, ha),
    };
  };

  const resultados = [...acc.entries()]
    .map(([id, r]) => build(id, r))
    .sort((a, b) => b.margenBruto - a.margenBruto || a.nombre.localeCompare(b.nombre));

  // Los totales se recalculan desde el agregado (no se suman los "por ha", que
  // no son aditivos: el total por ha es el total dividido por la superficie total).
  const totalAcc: Acc = {
    nombre: "",
    hectareas: [...acc.values()].reduce((s, r) => s + r.hectareas, 0),
    ingresos: [...acc.values()].reduce((s, r) => s + r.ingresos, 0),
    ingresosPresupuestados: [...acc.values()].reduce((s, r) => s + r.ingresosPresupuestados, 0),
    costosDirectos: [...acc.values()].reduce((s, r) => s + r.costosDirectos, 0),
    costosIndirectos: [...acc.values()].reduce((s, r) => s + r.costosIndirectos, 0),
    costosVariables: [...acc.values()].reduce((s, r) => s + r.costosVariables, 0),
    costosDirectosPresupuestados: [...acc.values()].reduce(
      (s, r) => s + r.costosDirectosPresupuestados,
      0
    ),
  };
  const { id: _id, nombre: _nombre, ...totales } = build("__total__", totalAcc);

  return { periodo, resultados, totales };
}

// ---------------------------------------------------------------------------
// #30 (opcional) — VALORIZACIÓN EN MONEDA SECUNDARIA
// ---------------------------------------------------------------------------

/** Los campos monetarios de `MargenResultado` (los % y las ha no se convierten). */
const CAMPOS_MONETARIOS = [
  "ingresos",
  "costosDirectos",
  "costosIndirectos",
  "costosVariables",
  "costosTotales",
  "margenBruto",
  "margenContribucion",
  "utilidad",
  "ingresosPresupuestados",
  "costosDirectosPresupuestados",
  "margenBrutoPresupuestado",
  "desvio",
  "ingresosPorHa",
  "costosDirectosPorHa",
  "margenBrutoPorHa",
  "margenContribucionPorHa",
  "desvioPorHa",
] as const;

/**
 * #30 · Convierte los montos de un resultado a una moneda secundaria dividiendo
 * por el tipo de cambio (unidades de moneda base por 1 de la secundaria).
 * Los porcentajes y las hectáreas quedan intactos: no son montos.
 * Un tipo de cambio no positivo devuelve el resultado sin tocar.
 */
export function convertirMoneda<T extends MargenResultado>(
  resultado: T,
  tipoCambio: number
): T {
  if (!Number.isFinite(tipoCambio) || tipoCambio <= 0) return resultado;
  const out = { ...resultado };
  for (const campo of CAMPOS_MONETARIOS) {
    const v = out[campo];
    if (typeof v === "number") {
      (out as Record<string, unknown>)[campo] = redondear(v / tipoCambio);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// #29 — EXPORTACIÓN DE REPORTES (CSV puro, sin dependencias)
// ---------------------------------------------------------------------------

export interface ColumnaExport<T> {
  key: string;
  label: string;
  value?: (row: T) => unknown;
}

/** Escapa una celda al dialecto CSV (RFC 4180): comillas, comas y saltos. */
export function escaparCsv(valor: unknown): string {
  if (valor === null || valor === undefined) return "";
  const s = String(valor);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * #29 · Serializa cualquier reporte a CSV (Excel-compatible). Puro: devuelve el
 * string; quien lo consume decide si lo descarga, lo adjunta o lo imprime.
 */
export function exportarCsv<T>(rows: readonly T[], columnas: readonly ColumnaExport<T>[]): string {
  const head = columnas.map((c) => escaparCsv(c.label)).join(",");
  const body = rows.map((row) =>
    columnas
      .map((c) => escaparCsv(c.value ? c.value(row) : (row as Record<string, unknown>)[c.key]))
      .join(",")
  );
  return [head, ...body].join("\r\n");
}

// ---------------------------------------------------------------------------
// CONTRATO DEL SERVICIO — las rutas del backend y la interfaz que el front
// implementa con su transporte (axios en apps/v5). El paquete no hace I/O.
// ---------------------------------------------------------------------------

/** Verbos planos expuestos por `functions/src/modules/costos`. */
export const COSTOS_ENDPOINTS = {
  /** Transacciones de costo real (#29). */
  costosList: "/costosList",
  costos: "/costos",
  /** Líneas del presupuesto anual (#28). */
  presupuestoList: "/presupuestoList",
  presupuesto: "/presupuesto",
  /** Ingresos por cultivo/unidad (#30). */
  ingresosList: "/ingresosList",
  ingresos: "/ingresos",
  /** Unidades productivas con superficie (#30). */
  unidadesList: "/costosUnidadesList",
  unidades: "/costosUnidades",
  /** Las cuatro colecciones en una sola llamada, para los reportes. */
  dataset: "/costosDataset",
} as const;

/** El dato crudo que alimenta los tres reportes del módulo. */
export interface CostosDataset {
  transacciones: CostoTransaccion[];
  presupuesto: PresupuestoLinea[];
  ingresos: IngresoRegistro[];
  unidades: UnidadProductiva[];
}

/**
 * Contrato del cliente de servicio. `apps/v5` lo implementa con axios; un
 * consumidor de test lo implementa con datos en memoria. El paquete solo
 * declara la forma: sin transporte, sin tenant, sin entorno.
 */
export interface CostosService {
  fetchDataset(filtro?: FiltroCostos): Promise<CostosDataset>;
  fetchTransacciones(filtro?: FiltroCostos): Promise<CostoTransaccion[]>;
  createTransaccion(payload: Partial<CostoTransaccion>): Promise<CostoTransaccion>;
  updateTransaccion(id: string, payload: Partial<CostoTransaccion>): Promise<CostoTransaccion>;
  deleteTransaccion(id: string): Promise<void>;
  fetchPresupuesto(filtro?: FiltroCostos): Promise<PresupuestoLinea[]>;
  createPresupuestoLinea(payload: Partial<PresupuestoLinea>): Promise<PresupuestoLinea>;
  updatePresupuestoLinea(id: string, payload: Partial<PresupuestoLinea>): Promise<PresupuestoLinea>;
  deletePresupuestoLinea(id: string): Promise<void>;
}

// ---------------------------------------------------------------------------
// VALIDACIÓN — pura, compartida por el form del front y el schema del back.
// ---------------------------------------------------------------------------

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

const esMontoValido = (n: unknown): n is number =>
  typeof n === "number" && Number.isFinite(n) && n >= 0;

/** `"2026"` o `"2026-07"`. */
export function esPeriodoValido(periodo: unknown): boolean {
  return typeof periodo === "string" && /^\d{4}(-(0[1-9]|1[0-2]))?$/.test(periodo);
}

/** Valida una transacción de costo antes de persistirla. */
export function validateCostoTransaccion(input: Partial<CostoTransaccion>): ValidationResult {
  const errors: string[] = [];
  if (!esPeriodoValido(input.periodo)) errors.push("periodo");
  if (!esMontoValido(input.monto)) errors.push("monto");
  if (!input.origen || !ORIGENES_COSTO.includes(input.origen)) errors.push("origen");
  if (typeof input.directo !== "boolean") errors.push("directo");
  return { valid: errors.length === 0, errors };
}

/** Valida una línea de presupuesto antes de persistirla. */
export function validatePresupuestoLinea(input: Partial<PresupuestoLinea>): ValidationResult {
  const errors: string[] = [];
  if (!esPeriodoValido(input.periodo)) errors.push("periodo");
  if (!esMontoValido(input.montoPresupuestado)) errors.push("montoPresupuestado");
  if (!input.centroCostoId) errors.push("centroCostoId");
  return { valid: errors.length === 0, errors };
}

/** Valida un ingreso antes de persistirlo. */
export function validateIngreso(input: Partial<IngresoRegistro>): ValidationResult {
  const errors: string[] = [];
  if (!esPeriodoValido(input.periodo)) errors.push("periodo");
  if (!esMontoValido(input.monto)) errors.push("monto");
  return { valid: errors.length === 0, errors };
}
